import {
  collection,
  query,
  getDocs,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  deleteDoc,
  orderBy,
} from 'firebase/firestore';
import { LANGUAGES } from './constants';
import { db } from './firebase';

export const delay = ms => new Promise(res => setTimeout(res, ms));

// Trims the payload of a Google (or Firebase Storage) profile photo by asking
// the CDN for a smaller derivative. Google's /a/ACg8... URLs accept =sN-c
// (square, N px). No-op for other hosts. Halves-to-tenths the bytes of the
// avatar strip when a match has 14-20 players.
export const thumbUrl = (url, sizePx = 96) => {
  if (!url || typeof url !== 'string') return url;
  // Google user content: strip existing size param and append the desired one.
  if (/googleusercontent\.com/.test(url)) {
    const base = url.split('=')[0];
    return `${base}=s${sizePx}-c`;
  }
  return url;
};

export const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Reverse-geocodes coordinates to a human-readable city name using OpenStreetMap's
// Nominatim service. No API key required (the Google Geocoding API is not enabled
// for our Maps key). Returns the city name, or null if it can't be resolved.
export const reverseGeocodeCity = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=10&accept-language=es`,
    );
    const data = await res.json();
    const a = data.address ?? {};
    return (
      a.city ||
      a.town ||
      a.village ||
      a.municipality ||
      a.county ||
      a.state ||
      null
    );
  } catch {
    return null;
  }
};

const normalizeToHex = color => {
  if (!color) return null;
  if (/^#[0-9A-Fa-f]{6}$/.test(color)) return color;
  if (/^#[0-9A-Fa-f]{8}$/.test(color)) return color.slice(0, 7);
  const m = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (m) return `#${[m[1], m[2], m[3]].map(n => Number(n).toString(16).padStart(2, '0')).join('')}`;
  return null;
};

const getLuminance = hex => {
  const clean = (hex || '').replace('#', '');
  if (clean.length !== 6) return 0.5;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const toLinear = c => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

// Returns an adjusted team color that's always readable against the current theme background.
// Normalizes rgb() and 8-char hex formats; falls back to grey when color is missing or invalid.
export const getReadableTeamColor = (color, isDark) => {
  const normalized = normalizeToHex(color) || (isDark ? '#4a4a4a' : '#6b7280');
  const lum = getLuminance(normalized);
  if (!isDark && lum > 0.85) return '#1f1f1f';
  if (isDark && lum < 0.15) return '#d0d0d0';
  return normalized;
};

export const onDocumentChange = (table, id, onUpdate) => {
  const docRef = doc(db, table, id);
  return onSnapshot(
    docRef,
    snapshot => {
      if (snapshot.exists()) onUpdate({ id: snapshot.id, ...snapshot.data() });
    },
    err => {
      if (err.code === 'permission-denied') return;
      console.error(`[Firestore] ${table}/${id} listener:`, err.code, err.message);
    },
  );
};

export const onCollectionChange = (table, setSnapshot, whereCondition) => {
  const q = whereCondition
    ? query(collection(db, table), whereCondition)
    : query(collection(db, table));
  return onSnapshot(
    q,
    querySnapshot => { setSnapshot(querySnapshot); },
    err => {
      if (err.code === 'permission-denied') return;
      console.error(`[Firestore] ${table} listener:`, err.code, err.message);
    },
  );
};

const queryByTable = (tableName, whereCondition, orderByField) => {
  if (whereCondition) {
    if (orderByField) {
      return query(
        collection(db, tableName),
        whereCondition,
        orderBy(orderByField),
      );
    }
    return query(collection(db, tableName), whereCondition);
  }
  if (orderByField) {
    return query(collection(db, tableName), orderBy(orderByField));
  }
  return query(collection(db, tableName));
};

export const fetchByTable = async params => {
  const {
    tableName,
    whereCondition,
    setLoading = () => {},
    orderByField = null,
  } = params;
  setLoading(true);
  const querySnapshot = await getDocs(
    queryByTable(tableName, whereCondition, orderByField),
  );
  const col = [];
  querySnapshot.forEach(d => {
    col.push({ ...d.data(), id: d.id });
  });
  setLoading(false);
  return col;
};

export const addDataToTable = async params => {
  const { tableName, data, setLoading = () => {} } = params;
  setLoading(true);
  try {
    const res = await addDoc(collection(db, tableName), data);
    return res;
  } finally {
    setLoading(false);
  }
};

export const addDataToTableWithCustomId = async params => {
  const { tableName, id, data, setLoading = () => {} } = params;
  setLoading(true);
  try {
    const res = await setDoc(doc(db, tableName, id), data);
    return res;
  } finally {
    setLoading(false);
  }
};

export const updateDataById = async params => {
  const { id, tableName, data, setLoading = () => {} } = params;
  setLoading(true);
  try {
    const docRef = doc(db, tableName, id);
    await updateDoc(docRef, data);
    return true;
  } finally {
    setLoading(false);
  }
};

export const deleteDocById = async params => {
  const { id, tableName, setLoading = () => {} } = params;
  setLoading(true);
  const docRef = doc(db, tableName, id);
  await deleteDoc(docRef);
  setLoading(false);
  return true;
};

export const deleteDocsByWhere = async params => {
  const { id, tableName, setLoading = () => {} } = params;
  setLoading(true);
  const docRef = doc(db, tableName, id);
  await deleteDoc(docRef);
  setLoading(false);
  return true;
};

const getLocaleLangByUserLanguage = userLanguage => {
  let lang = 'es-ES';
  if (userLanguage === LANGUAGES.SPANISH) {
    lang = 'es-ES';
  }
  if (userLanguage === LANGUAGES.ENGLISH) {
    lang = 'en-US';
  }
  if (userLanguage === LANGUAGES.ARABIC) {
    lang = 'ar';
  }
  return lang;
};

export const getDateFromTimestamp = timestamp =>
  `${new Date(timestamp?.toDate()).toLocaleDateString()} ${new Date(
    timestamp?.toDate(),
  ).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const capitalizeFirstLetter = string =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const getShiftDateFromTimestamp = (timestamp, language) => {
  const date = new Date(timestamp?.toDate());
  const dayOfWeek = capitalizeFirstLetter(
    date.toLocaleDateString(getLocaleLangByUserLanguage(language), {
      weekday: 'long',
    }),
  );
  const dayMonth = date.toLocaleDateString(
    getLocaleLangByUserLanguage(language),
    { day: 'numeric', month: 'numeric' },
  );
  const timePart = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${dayOfWeek} ${dayMonth} ${timePart}`;
};

export const getLeagueDateFromTimestamp = (timestamp, language) => {
  const date = new Date(timestamp?.toDate());
  const dayOfWeek = capitalizeFirstLetter(
    date.toLocaleDateString(getLocaleLangByUserLanguage(language), {
      weekday: 'long',
    }),
  );
  const dayMonth = date.toLocaleDateString(
    getLocaleLangByUserLanguage(language),
    { day: 'numeric', month: 'numeric' },
  );
  return `${dayOfWeek} ${dayMonth}`;
};

export const getTimerByDate = date => {
  if (!date) {
    return null;
  }
  const timePart = date.toLocaleTimeString([], {
    minute: '2-digit',
    second: '2-digit',
  });
  return `${timePart}''`;
};

export const copyText = (text, message) => {
  navigator.clipboard.writeText(text).then(
    () => message.success(`${text} copiado con exito`),
    () => err => message.error(err),
  );
};
